import { Row } from "../../../../core/components/Row";
import { TextComponent } from "../../../../core/components/TextComponent";
import { RootState } from "../../../../core/store/store";
import { useSelector } from "react-redux";
import { useDebouncedConfig } from "../hook/useDebounceConfig";
import "./AgeRangeSettings.css";

const MIN_AGE = 18;
const MAX_AGE = 100;

interface AgeRangeSettingsState {
	minAge: number;
	maxAge: number;
}

export const AgeRangeSettings = () => {
	const { getProfileConfig } = useSelector((state: RootState) => state.onboarding);
	const savedAgeRange = getProfileConfig?.search_filters?.age_range;

	if (!savedAgeRange || savedAgeRange.length < 2) {
		return null;
	}

	return (
		<AgeRangeSlider
			initialMinAge={savedAgeRange[0]}
			initialMaxAge={savedAgeRange[1]}
		/>
	);
};

interface AgeRangeSliderProps {
	initialMinAge: number;
	initialMaxAge: number;
}

const AgeRangeSlider = ({ initialMinAge, initialMaxAge }: AgeRangeSliderProps) => {
	const initialValues: AgeRangeSettingsState = {
		minAge: initialMinAge,
		maxAge: initialMaxAge,
	};
	const [ageRangeSettings, setAgeRangeSettings] = useDebouncedConfig<AgeRangeSettingsState>({
		initialValues,
		buildUpdatedFilters: (settings) => ({
			search_filters: {
				age_range: [settings.minAge, settings.maxAge],
			},
		}),
	});
	const handleAgeRangeChange = (key: keyof AgeRangeSettingsState, value: string) => {
		const newValue = parseInt(value);
		if (key === "minAge" && newValue > ageRangeSettings.maxAge) {
			setAgeRangeSettings({
				minAge: newValue,
				maxAge: newValue,
			});
		} else if (key === "maxAge" && newValue < ageRangeSettings.minAge) {
			setAgeRangeSettings({
				maxAge: newValue,
				minAge: newValue,
			});
		} else {
			setAgeRangeSettings({
				...ageRangeSettings,
				[key]: newValue,
			});
		}
	};

	const minPosition = ((ageRangeSettings.minAge - MIN_AGE) / (MAX_AGE - MIN_AGE)) * 100;
	const maxPosition = ((ageRangeSettings.maxAge - MIN_AGE) / (MAX_AGE - MIN_AGE)) * 100;
	const trackStyle = {
		background: `linear-gradient(to right, #e0e0e0 0%, #e0e0e0 ${minPosition}%, #e50aac ${minPosition}%, #e50aac ${maxPosition}%, #e0e0e0 ${maxPosition}%, #e0e0e0 100%)`,
	};

	return (
		<>
			<Row mainAxisAlignment="between" className="mt-7 mb-3">
				<TextComponent variant="h2" className="font-semibold">
					Age Range
				</TextComponent>
				<TextComponent variant="h3" id="age-range-value">
					{ageRangeSettings.minAge} – {ageRangeSettings.maxAge}
				</TextComponent>
			</Row>
			<div
				className="age-range-control mb-7"
				role="group"
				aria-labelledby="age-range-value"
			>
				<div className="age-range-track" style={trackStyle} aria-hidden="true" />
				<input
					type="range"
					className="age-range-input age-range-input-min"
					min={MIN_AGE}
					max={MAX_AGE}
					step={1}
					value={ageRangeSettings.minAge}
					onChange={(event) => handleAgeRangeChange("minAge", event.target.value)}
					aria-label="Minimum age"
					aria-valuetext={`${ageRangeSettings.minAge} years old`}
				/>
				<input
					type="range"
					className="age-range-input age-range-input-max"
					min={MIN_AGE}
					max={MAX_AGE}
					step={1}
					value={ageRangeSettings.maxAge}
					onChange={(event) => handleAgeRangeChange("maxAge", event.target.value)}
					aria-label="Maximum age"
					aria-valuetext={`${ageRangeSettings.maxAge} years old`}
				/>
			</div>
		</>
	);
};
